// Parent ↔ child binding + invite-code redemption shapes.
import { z } from 'zod';
import { DisplayName, Locale, Password, Username } from '../auth/requests.js';
import { Member } from './user.js';
// ---------------------------------------------------------------------------
// ParentChildBinding
// ---------------------------------------------------------------------------
export const ParentChildBinding = z.object({
    parentId: z.string().uuid(),
    childId: z.string().uuid(),
    /** Who created the binding. */
    createdBy: z.string().uuid(),
    /** Whether redeemed via code, assigned manually, or created by a parent. */
    channel: z.enum(['code', 'manual', 'parent-created']),
    createdAt: z.string().datetime({ offset: true }),
});
// ---------------------------------------------------------------------------
// InviteCode — issued by manager, redeemed by parent to bind a child.
// ---------------------------------------------------------------------------
export const InviteCode = z.object({
    id: z.string().uuid(),
    /** Redemption binds this child to the redeeming parent. */
    childId: z.string().uuid(),
    /** Role granted to the redeemer. */
    targetRole: z.literal('parent'),
    /** Issued by. */
    issuedBy: z.string().uuid(),
    /** Expiry — typically 24h from issue. */
    expiresAt: z.string().datetime({ offset: true }),
    /** Set on first successful redemption (codes are one-shot). */
    redeemedBy: z.string().uuid().nullable(),
    redeemedAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
});
// ---------------------------------------------------------------------------
// Request / response shapes for the parent-bind flow.
// ---------------------------------------------------------------------------
export const IssueInviteCodeRequest = z.object({
    childId: z.string().uuid(),
    ttlMinutes: z.number().int().min(5).max(10_080).optional().default(1_440),
});
export const RedeemCodeRequest = z.object({
    code: z.string().min(4).max(16),
});
export const IssueInviteCodeResponse = z.object({
    invite: InviteCode,
    rawCode: z.string().min(4).max(16),
});
export const RedeemCodeResponse = z.object({
    binding: ParentChildBinding,
    member: Member,
});
export const CreateChildAccountRequest = z
    .object({
    displayName: DisplayName,
    username: Username,
    password: Password,
    confirmPassword: z.string().min(1).max(256),
    locale: Locale.optional(),
})
    .superRefine((val, ctx) => {
    if (val.password !== val.confirmPassword) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['confirmPassword'],
            message: 'Passwords do not match',
            params: { code: 'AUTH_PASSWORD_MISMATCH' },
        });
    }
});
export const CreateChildAccountResponse = z.object({
    child: Member,
    binding: ParentChildBinding,
    member: Member,
});
export const ChildPasswordResetRequest = z.object({
    id: z.string().uuid(),
    childId: z.string().uuid(),
    childDisplayName: z.string(),
    status: z.enum(['pending', 'completed', 'expired']),
    expiresAt: z.string().datetime({ offset: true }),
    completedBy: z.string().uuid().nullable(),
    completedAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
});
export const ChildPasswordResetListResponse = z.object({
    requests: z.array(ChildPasswordResetRequest),
});
export const CompleteChildPasswordResetRequest = z
    .object({
    parentPassword: z.string().min(1).max(256),
    password: Password,
    confirmPassword: z.string().min(1).max(256),
})
    .superRefine((val, ctx) => {
    if (val.password !== val.confirmPassword) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['confirmPassword'],
            message: 'Passwords do not match',
            params: { code: 'AUTH_PASSWORD_MISMATCH' },
        });
    }
});
//# sourceMappingURL=binding.js.map